import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";
import { colors } from "@wfp/ui";

type DataPoint = {
  month: string;
  [key: string]: string | number;
};

type MiniBarChartSeries = {
  dataKey: string;
  label: string;
  color: string;
};

type MiniBarChartProps = {
  data: DataPoint[];
  boysLabel?: string;
  girlsLabel?: string;
  totalLabel: string;
  series?: MiniBarChartSeries[];
  isPercentage?: boolean;
  compactTooltip?: boolean;
  valueSuffix?: string;
};

const COLORS = {
  male: colors["male"]?.hex || "#0062c7",
  female: colors["female"]?.hex || "#f2545b",
};

const formatMonth = (month: string) => moment(month, "YYYY-MM").format("MMM");

const formatAxisValue = (value: number, isPercentage?: boolean) => {
  if (isPercentage) return `${value}%`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value.toString();
};

const MiniBarChartTooltip = ({
  active,
  payload,
  label,
  totalLabel,
  isPercentage,
  compactTooltip,
  valueSuffix,
}: any) => {
  if (!active || !payload || !payload.length) return null;

  if (compactTooltip) {
    return (
      <div className="dashboard-chart-tooltip">
        <p className="dashboard-chart-tooltip__label">
          {moment(label, "YYYY-MM").format("MMM YYYY")}
        </p>
        <p
          className="dashboard-chart-tooltip__item"
          style={{ color: payload[0].color }}
        >
          {payload[0].value.toLocaleString()}
          {isPercentage ? "%" : valueSuffix || ""}
        </p>
      </div>
    );
  }

  const total = payload.reduce(
    (sum: number, entry: any) => sum + Number(entry.value || 0),
    0
  );

  return (
    <div className="dashboard-chart-tooltip">
      <p className="dashboard-chart-tooltip__label">
        {moment(label, "YYYY-MM").format("MMM YYYY")}
      </p>
      {payload.map((entry: any) => (
        <p
          key={entry.dataKey}
          className="dashboard-chart-tooltip__item"
          style={{ color: entry.color }}
        >
          {entry.name}: {entry.value.toLocaleString()}
          {isPercentage ? "%" : valueSuffix || ""}
        </p>
      ))}
      <p className="dashboard-chart-tooltip__item">
        {totalLabel}: {total.toLocaleString()}
        {isPercentage ? "%" : valueSuffix || ""}
      </p>
    </div>
  );
};

const MiniBarChart: React.FC<MiniBarChartProps> = ({
  data,
  boysLabel,
  girlsLabel,
  totalLabel,
  series,
  isPercentage,
  compactTooltip,
  valueSuffix,
}) => {
  const chartSeries =
    series ||
    [
      {
        dataKey: "male",
        label: boysLabel,
        color: COLORS.male,
      },
      {
        dataKey: "female",
        label: girlsLabel,
        color: COLORS.female,
      },
    ].filter((entry): entry is MiniBarChartSeries => Boolean(entry.label));

  return (
    <ResponsiveContainer width="100%" height={120}>
      <BarChart data={data} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
        <XAxis
          dataKey="month"
          tickFormatter={formatMonth}
          tick={{ fontSize: 10 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          width={40}
          tickFormatter={(value: number) =>
            formatAxisValue(value, isPercentage)
          }
        />
        <Tooltip
          content={
            <MiniBarChartTooltip
              totalLabel={totalLabel}
              isPercentage={isPercentage}
              compactTooltip={compactTooltip}
              valueSuffix={valueSuffix}
            />
          }
        />
        {chartSeries.map((entry) => (
          <Bar
            key={entry.dataKey}
            dataKey={entry.dataKey}
            name={entry.label}
            stackId="dashboard-stack"
            fill={entry.color}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default MiniBarChart;
